<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-realtime-shipment">
    <nx-report-header title="实时发运监控">
      <div class="kb-header-actions">
        <span class="kb-toolbar-hint">业务日</span>
        <el-date-picker
          v-model="queryDate"
          type="date"
          value-format="yyyy-MM-dd"
          placeholder="业务日"
          size="small"
          style="width: 140px"
          @change="loadData"
        />
        <el-switch v-model="autoRefresh" active-text="30s 自动刷新" @change="onAutoRefreshChange" />
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadData">立即刷新</el-button>
      </div>
    </nx-report-header>

    <div class="kb-kpi-row cols-6">
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">今日计划车次</div>
        <div class="kb-kpi-value">{{ result.planVehicleCount != null ? result.planVehicleCount : '—' }}</div>
        <div class="kb-kpi-sub">汽运出库汇总</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">已完成发运</div>
        <div class="kb-kpi-value is-ok">{{ result.completedToday != null ? result.completedToday : '—' }}</div>
        <div class="kb-kpi-sub">{{ completedDeltaText }}</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">场内作业中</div>
        <div class="kb-kpi-value is-warn">{{ result.inYardCount != null ? result.inYardCount : '—' }}</div>
        <div class="kb-kpi-sub">已进门未出门</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">门外排队</div>
        <div class="kb-kpi-value">{{ result.outsideQueueToday != null ? result.outsideQueueToday : '—' }}</div>
        <div class="kb-kpi-sub">{{ waitSubText }}</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">今日发运量（吨）</div>
        <div class="kb-kpi-value is-ok">{{ formatTons(result.todayShippedTons) }}</div>
        <div class="kb-kpi-sub">{{ planRateSubText }}</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">平均作业时长</div>
        <div class="kb-kpi-value">
          {{ avgDurationText }}<span v-if="avgDurationText !== '—'" class="kb-unit">分</span>
        </div>
        <div class="kb-kpi-sub">{{ avgDurationSubText }}</div>
      </div>
    </div>

    <div class="kb-two-col">
      <div class="kb-chart-card">
        <div class="kb-chart-head">
          <span>小时发运趋势</span>
          <small>当日实际发运量（吨）</small>
        </div>
        <div ref="chartTrend" class="kb-chart-box" />
      </div>
      <div class="kb-chart-card">
        <div class="kb-chart-head">
          <span>通道占用与状态</span>
          <small>场内车辆分布</small>
        </div>
        <div ref="chartPie" class="kb-chart-box" />
      </div>
    </div>

    <div class="kb-section">
      <div class="kb-section-head">汽运作业流水线</div>
      <div class="rts-flow">
        <template v-for="slot in flowSlots">
          <div v-if="slot.type === 'arrow'" :key="slot.key" class="rts-flow-arrow">→</div>
          <div
            v-else
            :key="slot.key"
            :class="['rts-flow-step', { active: activeStageSet.has(slot.idx) }]"
          >
            <div class="n">{{ slot.count }}</div>
            <div class="t">{{ slot.label }}</div>
          </div>
        </template>
      </div>
      <div class="rts-feed" aria-label="实时事件流">
        <div v-for="(f, i) in result.feed" :key="i" class="rts-feed-item">
          <span>{{ f.message }}</span>
          <time>{{ f.time }}</time>
        </div>
        <div v-if="!result.feed || !result.feed.length" class="rts-feed-empty">暂无流水</div>
      </div>
    </div>

    <div class="kb-section">
      <div class="kb-section-head">发运监控明细</div>
      <el-table :data="result.details" border stripe size="small" style="width: 100%">
        <el-table-column prop="planName" label="计划名称" min-width="140">
          <template slot-scope="{ row }">{{ row.planName || '—' }}</template>
        </el-table-column>
        <el-table-column prop="planNo" label="计划编号" min-width="130">
          <template slot-scope="{ row }">{{ row.planNo || '—' }}</template>
        </el-table-column>
        <el-table-column prop="custName" label="客户名称" min-width="130">
          <template slot-scope="{ row }">{{ row.custName || row.shipper || '—' }}</template>
        </el-table-column>
        <el-table-column prop="contractName" label="合同名称" min-width="140">
          <template slot-scope="{ row }">{{ row.contractName || '—' }}</template>
        </el-table-column>
        <el-table-column prop="contractNo" label="合同编号" min-width="130">
          <template slot-scope="{ row }">{{ row.contractNo || '—' }}</template>
        </el-table-column>
        <el-table-column prop="plate" label="车号" min-width="110" />
        <el-table-column prop="driverName" label="司机" min-width="100">
          <template slot-scope="{ row }">{{ row.driverName || '—' }}</template>
        </el-table-column>
        <el-table-column prop="coalType" label="物料类型" min-width="110" />
        <el-table-column prop="grossWeight" label="毛重(吨)" width="105" align="right">
          <template slot-scope="{ row }">{{ formatWeight(row.grossWeight) }}</template>
        </el-table-column>
        <el-table-column prop="netWeight" label="净重(吨)" width="105" align="right">
          <template slot-scope="{ row }">{{ formatWeight(row.netWeight) }}</template>
        </el-table-column>
        <el-table-column prop="shipmentTime" label="发运时间" width="95">
          <template slot-scope="{ row }">{{ row.shipmentTime || row.entryTime || '—' }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="{ row }">
            <el-tag v-if="row.status === 'completed'" type="success" size="mini">已完成</el-tag>
            <el-tag v-else type="primary" size="mini">{{ row.phaseTag || '进行中' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="现场抓拍" width="92" align="center">
          <template slot-scope="{ row }">
            <el-image
              v-if="row.snapshotUrl"
              class="rts-snapshot"
              :src="row.snapshotUrl"
              :preview-src-list="[row.snapshotUrl]"
              fit="cover"
            >
              <div slot="error" class="rts-snapshot-empty">无图</div>
            </el-image>
            <div v-else class="rts-snapshot-empty">无图</div>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchRealTimeShipmentMonitor } from './api'

export default {
  name: 'KanbanRealTimeShipmentMonitor',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      queryDate: null,
      result: {
        flowStages: [],
        feed: [],
        details: [],
        hourlyTrend: { hours: [], values: [] },
        stagePie: [],
        activeStageIndexes: []
      },
      autoRefresh: false,
      refreshTimer: null,
      chartTrend: null,
      chartPie: null
    }
  },
  computed: {
    flowSlots() {
      const stages = this.result.flowStages || []
      const out = []
      stages.forEach((st, idx) => {
        if (idx > 0) {
          out.push({ type: 'arrow', key: `arr-${idx}` })
        }
        out.push({
          type: 'step',
          key: `st-${st.code}-${idx}`,
          idx,
          count: st.count,
          label: st.label
        })
      })
      return out
    },
    activeStageSet() {
      const arr = this.result.activeStageIndexes || []
      return new Set(arr)
    },
    avgDurationText() {
      const m = this.result.avgDurationMinutes
      if (m == null || Number(m) <= 0) return '—'
      return Number(m).toFixed(1)
    },
    completedDeltaText() {
      const t = this.result.completedToday
      const y = this.result.completedYesterday
      if (t == null || y == null) return '—'
      if (y === 0) {
        if (t === 0) return '较昨日持平'
        return '较昨日由 0 增长'
      }
      const pct = (((t - y) / y) * 100).toFixed(0)
      if (pct === '0' || pct === '-0') return '较昨日持平'
      const n = Number(pct)
      return `较昨日 ${n > 0 ? '+' : ''}${pct}%`
    },
    waitSubText() {
      const w = this.result.avgWaitOutsideMinutes
      if (w != null && Number(w) > 0) {
        return `平均等待 ${Number(w).toFixed(0)} 分钟（建档→进门）`
      }
      return '当日已建档尚未进门'
    },
    planRateSubText() {
      const r = this.result.planCompleteRatePercent
      if (r != null) return `计划完成率 ${Number(r).toFixed(1)}%（吨位）`
      return '无当日重叠计划吨位或未完成率计算'
    },
    avgDurationSubText() {
      const w = this.result.avgWaitOutsideMinutes
      let s = '进门→出门均值'
      if (w != null && Number(w) > 0) {
        s += ` · 门外等待均值 ${Number(w).toFixed(0)} 分`
      }
      return s
    }
  },
  mounted() {
    this.queryDate = this.formatDateStr(new Date())
    this.loadData()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
    window.removeEventListener('resize', this.onResize)
    this.disposeCharts()
  },
  methods: {
    formatDateStr(d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    formatTons(v) {
      if (v == null || v === '') return '—'
      const n = Number(v)
      if (Number.isNaN(n)) return '—'
      return n.toLocaleString('zh-CN', { maximumFractionDigits: 0 })
    },
    formatWeight(v) {
      if (v == null || v === '') return '—'
      const n = Number(v)
      if (Number.isNaN(n)) return '—'
      return n.toFixed(2)
    },
    onAutoRefreshChange(on) {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
      if (on) {
        this.refreshTimer = setInterval(() => this.loadData(), 30000)
      }
    },
    async loadData() {
      this.loading = true
      try {
        const params = {}
        if (this.queryDate) params.queryDate = this.queryDate
        const data = await fetchRealTimeShipmentMonitor(params)
        this.result = data || this.result
        this.$nextTick(() => this.renderCharts())
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    disposeCharts() {
      if (this.chartTrend) {
        this.chartTrend.dispose()
        this.chartTrend = null
      }
      if (this.chartPie) {
        this.chartPie.dispose()
        this.chartPie = null
      }
    },
    onResize() {
      if (this.chartTrend) this.chartTrend.resize()
      if (this.chartPie) this.chartPie.resize()
    },
    renderCharts() {
      if (!this.$refs.chartTrend || !this.$refs.chartPie) return
      this.disposeCharts()
      const trend = echarts.init(this.$refs.chartTrend)
      const pie = echarts.init(this.$refs.chartPie)
      this.chartTrend = trend
      this.chartPie = pie

      const ht = this.result.hourlyTrend || { hours: [], values: [] }
      trend.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: 48, right: 24, top: 28, bottom: 28 },
        xAxis: {
          type: 'category',
          data: ht.hours || [],
          axisLine: { lineStyle: { color: '#cbd5e1' } }
        },
        yAxis: {
          type: 'value',
          name: '吨',
          splitLine: { lineStyle: { type: 'dashed', color: '#e2e8f0' } }
        },
        series: [
          {
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: { width: 3, color: '#0284c7' },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(2, 132, 199, 0.35)' },
                { offset: 1, color: 'rgba(2, 132, 199, 0.02)' }
              ])
            },
            data: (ht.values || []).map((v) => Number(v) || 0)
          }
        ]
      })

      const slices = this.result.stagePie || []
      pie.setOption({
        tooltip: { trigger: 'item' },
        legend: { bottom: 0, textStyle: { fontSize: 11 } },
        series: [
          {
            type: 'pie',
            radius: ['42%', '68%'],
            center: ['50%', '44%'],
            itemStyle: { borderRadius: 8, borderColor: '#fff', borderWidth: 2 },
            label: { fontSize: 11 },
            data: slices.map((s) => ({
              value: Number(s.value) || 0,
              name: s.name,
              itemStyle: { color: s.color }
            }))
          }
        ]
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
